import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Loading, Card, Badge, Alert } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowBack, IconPackage, IconTruck, IconMapPin, IconCreditCard, IconAlertCircle } from '@tabler/icons-react'
import { useClient } from '@/stores/useClient.ts'
import { Order, OrderStatus } from '@/models/shop.ts'

export default function OrderDetailPage() {
  const navigate = useNavigate()
  const { driveId, orderId } = useParams<{ driveId: string; orderId: string }>()
  const client = useClient()
  const [order, setOrder] = useState<Order | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (orderId) {
      fetchOrder()
    }
  }, [orderId])

  const fetchOrder = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await client.getOrder(orderId!)
      setOrder(response.data)
    } catch (err) {
      console.error('Error fetching order:', err)
      setError('Fehler beim Laden der Bestellung')
    } finally {
      setIsLoading(false)
    }
  }


  const formatPrice = (price: string) => {
    return `€${parseFloat(price).toFixed(2)}`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PENDING:
        return 'warning'
      case OrderStatus.CONFIRMED:
        return 'info'
      case OrderStatus.IN_PRODUCTION:
        return 'primary'
      case OrderStatus.SHIPPED:
        return 'success'
      case OrderStatus.DELIVERED:
        return 'success'
      case OrderStatus.CANCELLED:
        return 'error'
      default:
        return 'neutral'
    }
  }

  const getStatusText = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PENDING:
        return 'Ausstehend'
      case OrderStatus.CONFIRMED:
        return 'Bestätigt'
      case OrderStatus.IN_PRODUCTION:
        return 'In Produktion'
      case OrderStatus.SHIPPED:
        return 'Versandt'
      case OrderStatus.DELIVERED:
        return 'Geliefert'
      case OrderStatus.CANCELLED:
        return 'Storniert'
      default:
        return status
    }
  }


  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading color="primary" />
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <IconAlertCircle size={80} className="text-error" />
          <h2 className="text-xl font-semibold text-error">{error || 'Bestellung nicht gefunden'}</h2>
          <Button
            color="primary"
            onClick={() => navigate(`/d/${driveId}/shop/orders`)}
          >
            Zurück zu Bestellungen
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <Button
          color="primary"
          variant="outline"
          size="sm"
          onClick={() => navigate(`/d/${driveId}/shop/orders`)}
        >
          <IconArrowBack size={20} />
          Zurück zu Bestellungen
        </Button>

        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">
            Bestellung #{order.order_number}
          </h1>
          <Badge color={getStatusColor(order.status)} size="lg">
            {getStatusText(order.status)}
          </Badge>
        </div>
      </div>

      {/* Order Status Alert */}
      {order.status === OrderStatus.CANCELLED && (
        <Alert status="error" className="mb-6">
          <IconAlertCircle size={20} />
          <span>Diese Bestellung wurde storniert.</span>
        </Alert>
      )}

      {order.status === OrderStatus.SHIPPED && order.tracking_number && (
        <Alert status="success" className="mb-6">
          <IconTruck size={20} />
          <span>Ihre Bestellung wurde versandt. Tracking-Nummer: {order.tracking_number}</span>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Order Items */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="bg-base-100 shadow-lg">
            <div className="card-body">
              <h2 className="card-title mb-4">
                <IconPackage size={24} />
                Bestellte Artikel
              </h2>

              <div className="space-y-4">
                {order.items.map((item) => (
                  <div key={item.id} className="flex gap-4 p-4 border rounded-lg">
                    <div className="flex-1">
                      <h3 className="font-semibold">{item.product_title}</h3>
                      {item.variation_title && (
                        <p className="text-sm text-gray-600">
                          Variante: {item.variation_title}
                        </p>
                      )}
                      <p className="text-sm text-gray-600">
                        SKU: {item.product_sku}
                      </p>
                      {item.product_description && (
                        <p className="text-sm text-gray-500 mt-1">
                          {item.product_description}
                        </p>
                      )}
                      {(item.property_snapshots.length) && (
                        <div className="mt-2">
                          <p className="text-sm text-gray-600">Eigenschaften:</p>
                          <ul className="text-sm text-gray-500 ml-4">
                            {item.property_snapshots.map((prop, index) => (
                              <li key={index}>
                                {prop.property_name}: {prop.property_value}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">
                        {formatPrice(item.unit_price)} × {item.quantity}{item.units > 1 ? ` (${item.units} Einheiten)` : ''}
                      </div>
                      <div className="text-lg font-bold">
                        {formatPrice(item.total_price)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Order Summary and Details */}
        <div className="space-y-4">
          {/* Order Summary */}
          <Card className="bg-base-100 shadow-lg">
            <div className="card-body">
              <h2 className="card-title mb-4">
                <IconCreditCard size={24} />
                Bestellübersicht
              </h2>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Zwischensumme:</span>
                  <span>{formatPrice(order.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Versand:</span>
                  <span>{formatPrice(order.shipping_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span>MwSt.:</span>
                  <span>{formatPrice(order.tax_amount)}</span>
                </div>
                <hr className="my-2" />
                <div className="flex justify-between font-bold text-lg">
                  <span>Gesamt:</span>
                  <span>{formatPrice(order.total_amount)}</span>
                </div>
              </div>

              <div className="mt-4 text-sm text-gray-600">
                <p>Bestellt am: {formatDate(order.created_at)}</p>
                <p>Zuletzt aktualisiert: {formatDate(order.updated_at)}</p>
              </div>

            </div>
          </Card>

          {/* Shipping Address */}
          <Card className="bg-base-100 shadow-lg">
            <div className="card-body">
              <h2 className="card-title mb-4">
                <IconMapPin size={24} />
                Lieferadresse
              </h2>

              <div className="text-sm">
                <p className="font-medium">{order.shipping_name}</p>
                <p>{order.shipping_address_line1}</p>
                {order.shipping_address_line2 && (
                  <p>{order.shipping_address_line2}</p>
                )}
                <p>{order.shipping_postal_code} {order.shipping_city}</p>
                <p>{order.shipping_country}</p>
              </div>
            </div>
          </Card>

          {/* Billing Address (if different) */}
          {order.billing_name && (
            <Card className="bg-base-100 shadow-lg">
              <div className="card-body">
                <h2 className="card-title mb-4">
                  <IconCreditCard size={24} />
                  Rechnungsadresse
                </h2>

                <div className="text-sm">
                  <p className="font-medium">{order.billing_name}</p>
                  <p>{order.billing_address_line1}</p>
                  {order.billing_address_line2 && (
                    <p>{order.billing_address_line2}</p>
                  )}
                  <p>{order.billing_postal_code} {order.billing_city}</p>
                  <p>{order.billing_country}</p>
                </div>
              </div>
            </Card>
          )}

          {/* Customer Notes */}
          {order.customer_notes && (
            <Card className="bg-base-100 shadow-lg">
              <div className="card-body">
                <h2 className="card-title mb-4">Anmerkungen</h2>
                <p className="text-sm">{order.customer_notes}</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
